import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Select, MenuItem,
  FormControl, InputLabel, InputAdornment, Chip, CircularProgress,
  Tooltip, TablePagination, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { getAllMessages } from '../services/api';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: WhatsAppIcon, color: '#25D366' },
  { value: 'email', label: 'Email', icon: EmailIcon, color: '#1565c0' },
  { value: 'sms', label: 'SMS', icon: SmsIcon, color: '#757575' },
];
const CHANNEL_MAP = Object.fromEntries(CHANNELS.map(c => [c.value, c]));

// Vocabulario ya usado por email_utils.py/AppointmentDialog.js/ComunicacionesTab.js
// — se muestra tal cual viene, solo con una etiqueta más legible.
const MESSAGE_TYPE_LABELS = {
  confirmacion_turno: 'Confirmación de turno',
  recordatorio_turno: 'Recordatorio de turno',
  cancelacion_turno: 'Cancelación de turno',
  receta: 'Receta',
  indicaciones: 'Indicaciones',
  recordatorio: 'Recordatorio',
  otro: 'Otro',
};

const ChannelChip = ({ channel }) => {
  const cfg = CHANNEL_MAP[channel] || { label: channel, color: '#757575', icon: null };
  const Icon = cfg.icon;
  return (
    <Chip
      size="small"
      icon={Icon ? <Icon sx={{ fontSize: '14px !important', color: `${cfg.color} !important` }} /> : undefined}
      label={cfg.label}
      variant="outlined"
      sx={{ height: 22, fontSize: '0.7rem', borderColor: cfg.color }}
    />
  );
};

const Communications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    q: '', channel: '', message_type: '', fecha_desde: '', fecha_hasta: '',
  });
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * rowsPerPage, limit: rowsPerPage };
      if (filters.q) params.q = filters.q;
      if (filters.channel) params.channel = filters.channel;
      if (filters.message_type) params.message_type = filters.message_type;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await getAllMessages(params);
      setRows(res.data?.items || []);
      setTotal(res.data?.total ?? 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (field) => (e) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setFilters(prev => ({ ...prev, q: val }));
    }, 350);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Comunicaciones</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: -1.5 }}>
        Todo lo enviado a pacientes desde la clínica — automático (emails de turno) y manual (WhatsApp, recetas, indicaciones).
      </Typography>

      {/* Filtros — en mobile cada control va en su propia fila a ancho
          completo, mismo criterio que Audit.js. */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: { sm: 'wrap' } }}>
        <TextField
          size="small"
          placeholder="Buscar por paciente…"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
          sx={{ flex: 1, minWidth: { sm: 220 } }}
        />

        <FormControl size="small" sx={{ minWidth: { sm: 150 } }}>
          <InputLabel>Canal</InputLabel>
          <Select value={filters.channel} label="Canal" onChange={setFilter('channel')}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {CHANNELS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
          <InputLabel>Tipo</InputLabel>
          <Select value={filters.message_type} label="Tipo" onChange={setFilter('message_type')}>
            <MenuItem value=""><em>Todos</em></MenuItem>
            {Object.entries(MESSAGE_TYPE_LABELS).map(([v, label]) => (
              <MenuItem key={v} value={v}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
            value={filters.fecha_desde} onChange={setFilter('fecha_desde')} sx={{ flex: { xs: 1, sm: 'initial' } }} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
            value={filters.fecha_hasta} onChange={setFilter('fecha_hasta')} sx={{ flex: { xs: 1, sm: 'initial' } }} />
        </Box>
      </Paper>

      {(() => {
        const formatFecha = (fecha) => new Date(fecha.endsWith('Z') ? fecha : fecha + 'Z').toLocaleString('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });

        if (loading) {
          return (
            <Paper sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Paper>
          );
        }
        if (rows.length === 0) {
          return (
            <Paper sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              Sin comunicaciones registradas
            </Paper>
          );
        }

        // Misma tabla→cards en mobile que Audit.js/Patients.js.
        if (isMobile) {
          return (
            <Stack spacing={1.5}>
              {rows.map(r => (
                <Paper key={r.id} sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">{formatFecha(r.sent_at)}</Typography>
                    <ChannelChip channel={r.channel} />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{r.patient_name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {MESSAGE_TYPE_LABELS[r.message_type] || r.message_type}
                    {r.sent_by_username && ` · ${r.sent_by_username}`}
                  </Typography>
                  {r.message_preview && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
                      "{r.message_preview}"
                    </Typography>
                  )}
                </Paper>
              ))}
              <TablePagination
                component={Paper}
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </Stack>
          );
        }

        return (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Enviado por</TableCell>
                    <TableCell>Vista previa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {formatFecha(r.sent_at)}
                      </TableCell>
                      <TableCell>{r.patient_name}</TableCell>
                      <TableCell>
                        <ChannelChip channel={r.channel} />
                      </TableCell>
                      <TableCell>{MESSAGE_TYPE_LABELS[r.message_type] || r.message_type}</TableCell>
                      <TableCell>
                        {r.sent_by_username || <Typography component="span" variant="caption" color="text.disabled">Automático</Typography>}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {r.message_preview ? (
                          <Tooltip title={r.message_preview} arrow>
                            <Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 240 }}>
                              {r.message_preview}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </Paper>
        );
      })()}
    </Box>
  );
};

export default Communications;
